import { useCart } from '../contexts/CartContext'
import { useState, useEffect } from 'react'

function CartIcon({ style = 'gothic' }) {
  const { getTotalItems } = useCart()
  const totalItems = getTotalItems()
  const [isAnimating, setIsAnimating] = useState(false)
  const [previousCount, setPreviousCount] = useState(0)

  // Trigger animation when cart count increases
  useEffect(() => {
    if (totalItems > previousCount && previousCount > 0) {
      setIsAnimating(true)
      const timer = setTimeout(() => setIsAnimating(false), 600)
      return () => clearTimeout(timer)
    }
    setPreviousCount(totalItems)
  }, [totalItems, previousCount])

  // Clean Gothic Cart - Bold, thick strokes, sharp edges
  const gothicCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="square" 
      strokeLinejoin="miter"
      className="cart-icon-svg"
    >
      <circle cx="9" cy="21" r="2"></circle>
      <circle cx="20" cy="21" r="2"></circle>
      <path d="M1 3h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
  )

  // Minimalist Skull Cart - Clean skull design
  const skullCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="cart-icon-svg"
    >
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="m1 1 4 4 13 0 3 8-13 0"></path>
      <circle cx="9" cy="9" r="1"></circle>
      <circle cx="15" cy="9" r="1"></circle>
      <path d="M9 13c0 1.5 1.5 2 3 2s3-.5 3-2"></path>
    </svg>
  )

  // Sharp Cross Cart - Clean geometric cross
  const crossCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="square" 
      strokeLinejoin="miter"
      className="cart-icon-svg"
    >
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="m1 1 4 4 13 0 3 8-13 0"></path>
      <path d="M12 2v20M2 12h20"></path>
    </svg>
  )

  // Clean Crown Cart - Simple crown design
  const crownCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="cart-icon-svg"
    >
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="m1 1 4 4 13 0 3 8-13 0"></path>
      <path d="M5 8l3 3 3-3 3 3 3-3"></path>
    </svg>
  )

  // Sharp Thorn Cart - Clean thorn pattern
  const thornCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="cart-icon-svg"
    >
      <circle cx="9" cy="21" r="2"></circle>
      <circle cx="20" cy="21" r="2"></circle>
      <path d="M1 3h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      <path d="M8 12l2-2 2 2 2-2 2 2" strokeWidth="2"></path>
    </svg>
  )

  // Alternative: Simple Bag Icon - Clean and modern
  const bagCartIcon = (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="cart-icon-svg"
    >
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <path d="M16 10a4 4 0 0 1-8 0"></path>
    </svg>
  )

  // Select icon based on style prop
  const getIcon = () => {
    switch (style) {
      case 'skull':
        return skullCartIcon
      case 'cross':
        return crossCartIcon
      case 'crown':
        return crownCartIcon
      case 'thorn':
        return thornCartIcon
      case 'bag':
        return bagCartIcon
      case 'gothic':
      default:
        return gothicCartIcon
    }
  }

  return (
    <div className={`cart-icon ${isAnimating ? 'cart-animate' : ''}`}>
      {getIcon()}
      {totalItems > 0 && (
        <span className={`cart-count ${isAnimating ? 'count-bounce' : ''}`}>{totalItems}</span>
      )}
    </div>
  )
}

export default CartIcon
